import { useState } from 'react';
import type { EmailFormData } from '../components/SendEmailModal';

export interface UseEmailExportResult {
  isModalOpen: boolean;
  isSending: boolean;
  error: string | null;
  modalData: EmailModalData | null;
  openModal: (data: EmailModalData) => void;
  closeModal: () => void;
  sendEmail: (formData: EmailFormData) => Promise<void>;
}

export interface EmailModalData {
  subject: string;
  body: string;
  attachmentNames: string[];
  sendFn: (data: EmailFormData) => Promise<void>;
}

/**
 * Hook for managing email export workflow.
 * Handles modal state, sending emails, and error handling.
 *
 * Usage:
 * ```tsx
 * const emailExport = useEmailExport();
 *
 * const handleEmailClick = () => {
 *   const content = generateBulkWineEmailContent(wines);
 *   emailExport.openModal({
 *     subject: content.subject,
 *     body: content.body,
 *     attachmentNames: ['bulk-wines.zip'],
 *     sendFn: async (formData) => {
 *       await bulkWinesService.emailMultiple({ ...formData, ids: wineIds });
 *     },
 *   });
 * };
 * ```
 */
export const useEmailExport = (): UseEmailExportResult => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalData, setModalData] = useState<EmailModalData | null>(null);

  const openModal = (data: EmailModalData) => {
    setModalData(data);
    setIsModalOpen(true);
    setError(null);
  };

  const closeModal = () => {
    if (!isSending) {
      setIsModalOpen(false);
      setModalData(null);
      setError(null);
    }
  };

  const sendEmail = async (formData: EmailFormData) => {
    if (!modalData) {
      throw new Error('No modal data available');
    }

    setIsSending(true);
    setError(null);

    try {
      await modalData.sendFn(formData);
      // Success - modal will close automatically
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send email. Please try again.';
      setError(errorMessage);
      // Re-throw to let modal handle the error display
      throw new Error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  return {
    isModalOpen,
    isSending,
    error,
    modalData,
    openModal,
    closeModal,
    sendEmail,
  };
};
